import * as React from "react";
import Logo from "@batmaid/design-tokens/logo";

import { PATHS, ROUTES } from "@config/app.routing";
import { HOMEPAGE_V2 } from "@config/testIds/app";

import { Arrow as ArrowIcon } from "@ui/Assets/MyIcons";
import { TestWrapper } from "@ui/Atoms";

import { createHref } from "@services/CreateHref";

import { Markdown } from "@containers/Markdown";

import { useTranslate } from "@hooks/useTranslate";
import { useFeatureToggle } from "@hooks/useFeatureToggle";

import { Props } from "./Footer.types";
import * as Styled from "./Footer.styled";
import { CountrySelector } from "./CountrySelector";
import { CountriesList } from "./CountriesList";
import { OfficesList } from "./OfficesList";
import { Links } from "./Links";
import { Cities } from "./Cities";
import { AppReviewPayments } from "./AppReviewPayments";
import { LegalSocials } from "./LegalSocials";
import { CountrySelectorSSR } from "./CountrySelectorSSR";

/**
 * @author tomekbuszewski
 * @author jakub-bujakowski
 * @since 2019-3-20
 * DISCLAIMER:
 * This is a one-of-a-kind component in the app.
 * It uses injected <style></style> insted of StyledComponents
 * in order to encapsule styling and make this component reusable outside of React.
 * We are using Gatsby to render this component as plain HTML + CSS and then
 * embedding it in the monolith (Symfony) app, on our blog and Instapages.
 *
 * @since 2025-5-29
 * Footer was refactored, data transferred from Prismic to translations.
 * Styling with styled-components is working
 * JS still doesn't work on (Symfony) app, because gatsby try to redirect it to not found page
 */
const Footer = (props: Props): React.ReactElement => {
  const translate = useTranslate("batmaid_pwa_footer");
  const navTranslate = useTranslate("batmaid_pwa_navigation");

  const isGiftCardsEnabled = useFeatureToggle("gift_cards");

  const giftCardsLink = `${createHref(props.language, PATHS.CLIENT_APP)}#${
    ROUTES.GIFT_CARDS
  }`;

  return (
    <TestWrapper testId="footer">
      <Styled.Root className="footer-universal">
        <Styled.LogoContainer>
          <a
            href={`/${props.language}/`}
            aria-label={translate("home_link_area_label")}
          >
            <Logo.White width={126} />
          </a>
          <CountrySelectorSSR country={props.country}>
            <CountrySelector country={props.country}>
              <CountriesList />
            </CountrySelector>
          </CountrySelectorSSR>
        </Styled.LogoContainer>

        <Markdown
          components={{
            a: props => (
              <TestWrapper testId={HOMEPAGE_V2.FOOTER_HELP_BUTTON}>
                <Styled.HelpButton {...props}>
                  <span>{props.children}</span>
                  <ArrowIcon width={24} />
                </Styled.HelpButton>
              </TestWrapper>
            ),
          }}
        >
          {translate("help_link")}
        </Markdown>

        <Styled.MenuWrapper>
          <TestWrapper testId={HOMEPAGE_V2.FOOTER_NAVIGATION_COMPANY}>
            <Styled.LinksContainer>
              <Links
                title={translate("company_links_header")}
                links={translate("company_links")}
              />
            </Styled.LinksContainer>
          </TestWrapper>

          <TestWrapper testId={HOMEPAGE_V2.FOOTER_NAVIGATION_SERVICES}>
            <Styled.LinksContainer>
              <Links
                title={translate("services_links_header")}
                links={translate("services_links")}
                extraLinks={
                  isGiftCardsEnabled
                    ? [
                        {
                          href: giftCardsLink,
                          label: navTranslate("gift_cards"),
                          testId: "giftCardsLink",
                        },
                      ]
                    : undefined
                }
              />
            </Styled.LinksContainer>
          </TestWrapper>

          <TestWrapper testId={HOMEPAGE_V2.FOOTER_OFFICES}>
            <Styled.OfficesContainer>
              <OfficesList />
            </Styled.OfficesContainer>
          </TestWrapper>

          <Styled.AppReviewPaymentsContainer>
            <AppReviewPayments />
          </Styled.AppReviewPaymentsContainer>
        </Styled.MenuWrapper>

        <Cities />

        <LegalSocials country={props.country} />
      </Styled.Root>
    </TestWrapper>
  );
};

export { Footer };

Footer.displayName = "Footer";
